import type { ReportCard } from '@/store/api';

/*
  ═════════════════════════════════════════════════════════════════════════════
  ONE REPORT CARD, ON PAPER

  Shared by the school's print page and the parent's, deliberately: a parent
  holding a printout and a head teacher holding one should be holding the same
  document. Two copies of this markup is how the parent's quietly loses the
  grading key, or keeps a heading the school's has corrected.

  Everything it needs is on the card — including the school's own name, which
  a parent cannot fetch from the admin endpoint the school-side page used to
  read it from.
  ═════════════════════════════════════════════════════════════════════════════
*/

const TERMS: Record<string, string> = {
  term_1: 'First Term',
  term_2: 'Second Term',
  term_3: 'Third Term',
};

export function ordinal(n: number): string {
  const rest = n % 100;
  if (rest >= 11 && rest <= 13) return `${n}th`;
  return `${n}${['th', 'st', 'nd', 'rd'][n % 10] ?? 'th'}`;
}

/*
  How the two marks were combined, read off the card.

  A parent comparing the columns to the total deserves to know how the two were
  put together — it is the difference between 24+67 and 24% of 30 — and the
  card is the only thing either caller is guaranteed to have.
*/
function weightingLine(card: ReportCard): string {
  return card.markScale === 'weight'
    ? `Class score out of ${card.classScoreWeight}, examination out of ${card.examScoreWeight}`
    : `${card.classScoreWeight}% class score · ${card.examScoreWeight}% examination`;
}

export function ReportCardSheet({ card }: { card: ReportCard }) {
  const term = TERMS[card.term] ?? card.term;
  const weighting = weightingLine(card);
  const school = card.school;
  return (
    <article className="print-sheet mx-auto mb-6 w-[820px] max-w-full bg-white p-10 shadow-sm print:mb-0 print:w-full print:p-0 print:shadow-none">
      {/* ── Heading ─────────────────────────────────────────────────────── */}
      <header className="border-b-[3px] border-black pb-3 text-center">
        {school?.logoUrl && (
          <img src={school.logoUrl} alt="" className="mx-auto mb-2 h-14 w-14 object-contain" />
        )}
        <h1 className="text-[20px] font-bold uppercase tracking-[0.06em]">
          {school?.name ?? 'School report'}
        </h1>
        {(school?.address || school?.phone) && (
          <p className="mt-0.5 text-[10.5px] text-neutral-700">
            {[school?.address, school?.phone].filter(Boolean).join(' · ')}
          </p>
        )}
        <p className="mt-2 text-[12.5px] font-bold uppercase tracking-[0.1em]">
          {/* "Terminal" means end of term. A mid-term headed that way is a
              sheet that lies to a parent about what they are holding. */}
          {card.heading} · {term} · {card.academicYear}
        </p>
        <p className="text-[10.5px] text-neutral-700">{card.examName}</p>
      </header>

      {/* ── Who this is about ───────────────────────────────────────────── */}
      <dl className="mt-3 flex items-end justify-between gap-6 border-b border-neutral-400 pb-3">
        <Pair label="Name" value={card.studentName} />
        <Pair label="Class" value={card.studentClass || '—'} />
        <Pair
          label="Position in class"
          value={card.position > 0 ? `${ordinal(card.position)} of ${card.classSize}` : '—'}
          align="right"
        />
      </dl>

      {/* ── Subjects ────────────────────────────────────────────────────── */}
      <table className="mt-3 w-full border-collapse text-[11.5px]">
        <thead>
          <tr className="border-y border-black">
            <th className="py-1.5 pr-2 text-left font-bold uppercase tracking-wide">Subject</th>
            <th className="w-[66px] py-1.5 text-right font-bold uppercase tracking-wide">Class</th>
            <th className="w-[66px] py-1.5 text-right font-bold uppercase tracking-wide">Exam</th>
            <th className="w-[66px] py-1.5 text-right font-bold uppercase tracking-wide">Total</th>
            <th className="w-[56px] py-1.5 text-center font-bold uppercase tracking-wide">Grade</th>
            <th className="w-[62px] py-1.5 text-right font-bold uppercase tracking-wide">Pos</th>
            <th className="w-[110px] py-1.5 pl-2 text-left font-bold uppercase tracking-wide">Remark</th>
          </tr>
        </thead>
        <tbody>
          {card.subjects.map((s) => (
            <tr key={s.subjectId} className="border-b border-neutral-300">
              <td className="py-1.5 pr-2 font-semibold">{s.subject}</td>
              <td className="py-1.5 text-right tabular-nums">{s.classScore}</td>
              <td className="py-1.5 text-right tabular-nums">{s.examScore}</td>
              <td className="py-1.5 text-right font-bold tabular-nums">{s.total}</td>
              <td className="print-exact py-1.5 text-center font-bold">{s.grade}</td>
              <td className="py-1.5 text-right tabular-nums">
                {ordinal(s.position)}
                <span className="text-neutral-500">/{s.classSize}</span>
              </td>
              <td className="py-1.5 pl-2 text-neutral-700">{s.remark}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-black">
            <td className="py-1.5 pr-2 font-bold uppercase tracking-wide">
              Total · {card.subjectsTaken} subject{card.subjectsTaken === 1 ? '' : 's'}
            </td>
            <td colSpan={2} />
            <td className="py-1.5 text-right font-bold tabular-nums">{card.totalScore}</td>
            <td className="print-exact py-1.5 text-center font-bold">{card.overallGrade}</td>
            <td colSpan={2} className="py-1.5 pl-2 text-[10.5px] text-neutral-700">
              Average {card.average}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* A parent comparing the columns to the total deserves to know how the
          two were combined — it is the difference between 24+67 and 24% of 30. */}
      {weighting && <p className="mt-1.5 text-[9.5px] text-neutral-500">{weighting}.</p>}

      {/* ── Remarks and signatures ──────────────────────────────────────── */}
      <div className="print-keep mt-4 space-y-3">
        <Remark label="Overall" text={card.overallRemark} />
        <Remark label="Class teacher's remark" text={card.classTeacherRemark} />
        <Remark label="Head teacher's remark" text={card.headTeacherRemark} />
      </div>

      {/*
        The key, from the scale THIS exam was marked on.

        What a Ghanaian report card carries anyway, and the plainest possible
        demonstration of the promise: an old card shows the boundaries it was
        actually graded against, whatever the school has moved to since.
      */}
      {card.gradeBands.length > 0 && (
        <div className="print-keep mt-4 border-t border-neutral-300 pt-2">
          <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-neutral-500">Grading</p>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
            {[...card.gradeBands]
              .sort((a, b) => b.minScore - a.minScore)
              .map((band, i, all) => {
                const ceiling = i === 0 ? 100 : all[i - 1]!.minScore - 1;
                return (
                  <span key={band.grade} className="text-[9.5px] text-neutral-700">
                    <strong>{band.grade}</strong> {band.minScore}–{ceiling} {band.remark}
                  </span>
                );
              })}
          </div>
        </div>
      )}

      <div className="print-keep mt-8 grid grid-cols-2 gap-10">
        <Signature label="Class teacher" signatory={card.classTeacher} />
        <Signature label="Head teacher" signatory={card.headTeacher} />
      </div>
    </article>
  );
}

function Pair({ label, value, align }: { label: string; value: string; align?: 'right' }) {
  return (
    <div className={align === 'right' ? 'text-right' : ''}>
      <dt className="text-[9px] font-bold uppercase tracking-[0.1em] text-neutral-500">{label}</dt>
      <dd className="text-[13px] font-bold">{value}</dd>
    </div>
  );
}

function Remark({ label, text }: { label: string; text: string }) {
  return (
    <div>
      <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-neutral-500">{label}</p>
      {/* An empty ruled line, because a head teacher writing one in by hand is
          the normal case and a blank gap gives them nowhere to write. */}
      <p className="mt-0.5 min-h-[18px] border-b border-dotted border-neutral-400 pb-0.5 text-[11.5px]">
        {text}
      </p>
    </div>
  );
}

/*
  The signature of the person who actually signed, or a line for them to sign.

  The rule stays either way — a signature image sits ON it rather than
  replacing it, which is what a signature does on paper, and means a card
  where only one of the two has signed does not look half-built.

  `print-exact` because a browser printing "background graphics off" would
  otherwise drop the image and leave the name floating above an empty rule.
*/
function Signature({
  label,
  signatory,
}: {
  label: string;
  signatory: { fullName: string; signatureUrl: string | null } | null;
}) {
  return (
    <div>
      <div className="flex h-9 items-end justify-start border-b border-black">
        {signatory?.signatureUrl && (
          <img
            src={signatory.signatureUrl}
            alt=""
            className="print-exact max-h-[34px] max-w-[85%] object-contain object-left"
          />
        )}
      </div>
      <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.1em] text-neutral-500">{label}</p>
      {signatory?.fullName && <p className="text-[10.5px] font-semibold">{signatory.fullName}</p>}
    </div>
  );
}
